import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  GetBookKeepingByIdApi,
  GetBookKeepingQuestionListApi,
} from "../../../api/request/documents/documents";
import columns from "../../../columns/bookKeeping/bookKeeping-details";
import toast from "react-hot-toast";
import SkeletonTable from "../../../components/table/SkeletonTable";
import ViewBookKeepingModal from "./ViewBookKeepingModal";
import BackButton from "../../../components/button/BackButton";
import moment from "moment";
import Eye from "../../../assets/View";
import { Button } from "antd";
import { SubmitFileApi } from "../../../api/request/service";
import UploadBookKeeping from "./UploadBookKeeping";
import ModalComponent from "../../../components/modals/ModalComponent";

const BookKeepingDetails = () => {
  const { state } = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [isBookOpen, setIsBookOpen] = useState(false);
  const [selectedViewData, setSelectedViewData] = useState(null);
  const [IsReUploadOpen, setIsReUploadOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [questionList, setQuestionList] = useState([]);
  const [isQuestionLoading, setIsQuestionLoading] = useState(false);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [isReturnFileOpen, setIsReturnFileOpen] = useState(false);

  const getBookKeeping = async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      const { data } = await GetBookKeepingByIdApi({ file_id: id });
      setData(
        data?.data?.map((item) => {
          return {
            ...item,
            submittedDate: item?.submitted_date
              ? moment(item?.submitted_date).format("lll")
              : "--",
            completionDocument: item?.completion_document ? (
              <p
                className="text-blue-500 underline"
                onClick={() =>
                  window?.open(item?.completion_document, "_blank")
                }
              >
                View
              </p>
            ) : (
              "--"
            ),
            completionNotes: (
              <p className="w-[150px] truncate" title={item?.completion_notes}>
                {item?.completion_notes || "--"}
              </p>
            ),
            completedDate: item?.completed_date
              ? moment(item?.completed_date).format("lll")
              : "--",
            status: (
              <p
                className={`font-bold uppercase ${
                  item?.status === "pending"
                    ? "text-orange-500"
                    : item?.status === "completed"
                    ? "text-green-500"
                    : "text-red-500"
                }`}
              >
                {item?.status === "returned" ? (
                  <div
                    className="flex items-center gap-2"
                    onClick={(e) => {
                      e?.stopPropagation();
                      setIsReturnFileOpen(true);
                      setSelectedFile(item);
                    }}
                  >
                    <p
                      className={`font-bold uppercase ${
                        item?.status === "completed"
                          ? "text-green-500"
                          : "text-red-500"
                      }`}
                    >
                      {item?.status}
                    </p>
                    <img
                      src="/assets/icons/info.svg"
                      className="size-4 cursor-pointer"
                      title={item?.return_reason}
                    />
                  </div>
                ) : (
                  item?.status
                )}
              </p>
            ),
            action: (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    setIsBookOpen(true);
                    setSelectedViewData(item);
                  }}
                >
                  <Eye />
                </button>
                {item?.status === "returned" && (
                  <Button
                    className="btn-primary !px-2 !py-1 sm:gap-3 gap-1 !text-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(item);
                      setIsReUploadOpen(true);
                    }}
                  >
                    Upload
                  </Button>
                )}
              </div>
            ),
          };
        })
      );

      setIsLoading(false);
    } catch (error) {
      if (error?.response?.data?.message)
        toast?.error(error?.response?.data?.message);
      setIsLoading(false);
    }
  };

  const getReUploadFileList = async () => {
    if (!selectedFile) return;
    try {
      setIsQuestionLoading(true);
      const { data } = await GetBookKeepingQuestionListApi({
        file_id: selectedFile?.file_id,
      });
      setQuestionList(data?.data);
    } catch (error) {
      if (error?.response?.data?.message)
        toast?.error(error?.response?.data?.message);
    } finally {
      setIsQuestionLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setIsLoadingSubmit(true);
      let params = {
        file_id: selectedFile?.file_id,
      };

      const { data } = await SubmitFileApi(params);
      if (data?.status) {
        getBookKeeping();
        setIsReUploadOpen(false);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message);
    } finally {
      setIsLoadingSubmit(false);
    }
  };

  useEffect(() => {
    getBookKeeping();
  }, [id]);

  useEffect(() => {
    getReUploadFileList();
  }, [selectedFile]);

  return (
    <>
      <div className="bg-white rounded-lg border border-1 mb-5 p-5">
        <div className="flex items-center gap-3 mb-5">
          <BackButton />
          <div className="flex justify-between items-center gap-3 w-full">
            <h2 className="title">
              Book Keeping {data?.length ? `(${data?.length})` : ""}
            </h2>
            <button
              className="btn-primary !px-3"
              onClick={() => navigate(`/file/${id}`)}
            >
              View File Details
            </button>
          </div>
        </div>
        <SkeletonTable columns={columns} data={data} isLoading={isLoading} />
      </div>

      <ViewBookKeepingModal
        data={data}
        isLoading={isLoading}
        isOpen={isBookOpen}
        setIsOpen={setIsBookOpen}
        selectedData={selectedViewData}
        userId={state?.client?.user_id}
      />

      <UploadBookKeeping
        isOpen={IsReUploadOpen}
        setIsOpen={setIsReUploadOpen}
        questionList={questionList}
        isQuestionLoading={isQuestionLoading}
        isLoadingSubmit={isLoadingSubmit}
        handleSubmit={handleSubmit}
      />

      <ModalComponent
        title="Return File Reason"
        isOpen={isReturnFileOpen}
        setIsOpen={setIsReturnFileOpen}
      >
        <p className="text-base text-center">{selectedFile?.return_reason}</p>
      </ModalComponent>
    </>
  );
};

export default BookKeepingDetails;
